/**
 * The whole assignment decision: a selection goes in, and either the request body the backend
 * accepts comes out, or every reason it would not have.
 *
 * **One entry point, not five predicates.** The dialog could ask "is the date in range?", "are there
 * times?", "is this coordinate real?" separately and assemble the body itself — and then the
 * COMBINATION, which is the actual decision, would live in a component where no test can reach it.
 * `assignmentRequest` is the interface; everything below it is implementation.
 *
 * ── Refusing before the wire, and why each refusal is ours to make ───────────────────────────
 * Three of the four rules here are the backend's own, mirrored so the trainer hears them while the
 * dialog is still open rather than after a four-step flow ends in a 400:
 *
 * · **Start date.** `PlanAssignmentProcessor.EnsureDateInBounds` rejects anything earlier than
 *   `now − 1 day` or later than `now + 730 days`. The start date is normalised to midnight before
 *   that comparison, so "yesterday at midnight" is always more than a day before "now" and the
 *   window is, in calendar terms, **today through today + 730 days**. Expressed that way here
 *   because a date input takes calendar bounds, and because "one day of slack" is a clock-skew
 *   allowance rather than an invitation to back-date a plan.
 * · **Occurrence override dates** run through the same window, for the same reason.
 * · **A Stationary assignment needs at least one time.** `PostPlanAssignmentRequest.Validate`
 *   returns a validation error when the list is empty, and that one really is a 400.
 *
 * All three are about a DATED plan. A self-paced one takes no scheduling input at all — see
 * `selfPacedBody` — so none of them has anything to judge and the only refusal left is "pick a
 * client".
 *
 * The fourth is OURS, and is stated as such: a time keyed on a slot this plan does not have is
 * **silently ignored** by the backend rather than rejected (`ApplyStationaryTime` simply never
 * looks it up). Sending one would mean the trainer sets 07:00 for Thursday, the plan never trains
 * on Thursday, and nothing anywhere says so. Refusing is the honest answer, and it is unreachable
 * through the dialog's own inputs — so it fires only when something upstream is wrong.
 *
 * ── A stale coordinate is dropped, never sent ────────────────────────────────────────────────
 * An override whose coordinate is not among the generated occurrences is a **guaranteed 400** that
 * names coordinates the trainer never saw. Changing the start date regenerates dates but not
 * coordinates, so this is close to unreachable too — but it is one line, and the alternative
 * failure is the worst one this flow has.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. Refusals leave as data.
 */

import type {
  TrainWithMeModulesTrainingDtosRequestsOccurrenceDateOverrideInput as OccurrenceDateOverrideInput,
  TrainWithMeModulesTrainingDtosRequestsPostPlanAssignmentRequest as PostPlanAssignmentRequest,
  TrainWithMeModulesTrainingDtosRequestsStationaryTimeInput as StationaryTimeInput,
} from '~/shared/api/generated/model';

import {
  buildScheduleCycles,
  coordinateKey,
  flattenOccurrences,
  toCoordinate,
  type OccurrenceCoordinate,
  type ScheduleCycle,
} from './assignSchedule';
import { addDays, atMidnight, toIsoDate } from '~/shared/lib/calendarDate';
import { isTimeOfDay, stationaryTimeSlots } from './stationaryTimes';
import type { AssignSource, AssignTarget } from './assignTarget';

/**
 * Where the plan came from, in the backend's own `PlanSource` spelling, and what each declaration
 * is allowed to travel with.
 *
 * The origin has no default — `PostPlanAssignmentRequest.Validate` refuses a body that does not say
 * one (training-plan R13) — and `ValidateOriginPairing` refuses every mispairing, so each arm is
 * written out with its OMISSIONS visible, exactly as mobile's `buildAssignmentBody` writes them:
 *
 * · `LibraryTemplate` — the library entry as it stands. The fork happens server-side, so `plan` and
 *   `alsoSaveAsTemplate` are OMITTED; that arm refuses both outright.
 * · `CopyOfTemplate` — an unsaved copy: the template it descends from AND the whole tree.
 * · `Authored` — an unsaved plan from nothing: the tree, and NO `sourceTemplateId` key at all, which
 *   the backend refuses on this arm because a plan written from scratch descends from nothing.
 *
 * `alsoSaveAsTemplate` travels only on the two unsaved arms and only when the trainer ticked it,
 * as `true`: an absent flag is the backend's own `false`, and naming a default the server owns is
 * what this client never does. R13: the saved entry then becomes the plan's lineage.
 *
 * Shared by the dated and the self-paced body, so the two cannot disagree about an origin.
 */
function originFields(
  source: AssignSource,
  alsoSaveAsTemplate: boolean,
): Pick<PostPlanAssignmentRequest, 'source' | 'sourceTemplateId' | 'plan' | 'alsoSaveAsTemplate'> {
  const saved = alsoSaveAsTemplate ? { alsoSaveAsTemplate: true } : {};
  switch (source.kind) {
    case 'library':
      return { source: 'LibraryTemplate', sourceTemplateId: source.templateId };
    case 'copyOf':
      return {
        source: 'CopyOfTemplate',
        sourceTemplateId: source.templateId,
        plan: source.plan,
        ...saved,
      };
    case 'authored':
      return { source: 'Authored', plan: source.plan, ...saved };
  }
}

/**
 * The backend's `ExecutionMode` tokens, verbatim. These EXACT strings are parsed server-side —
 * never localise one, never lower-case one.
 */
export type AssignExecutionMode = 'Stationary' | 'Remote' | 'Elastic';

/**
 * The two modes the dialog OFFERS, in the mockup's own order.
 *
 * **Elastic is withdrawn from the offer and keeps its wire token.**
 * `AssignmentScheduleInputValidation.cs:66-70` refuses it outright, so a third row was an option
 * that could only ever produce a 400 — and the elastic-mode withdrawal effort of 19 August 2026
 * took it out of the mobile app for the same reason. The TYPE keeps the member because an
 * assignment created before the withdrawal still carries it on the wire and this app reads those
 * rows; what is gone is the trainer being able to choose it.
 *
 * Remote leads, because variant D makes it the default (see `DEFAULT_EXECUTION_MODE`).
 */
export const ASSIGN_EXECUTION_MODES: readonly AssignExecutionMode[] = ['Remote', 'Stationary'] as const;

/**
 * What the dialog opens on, and it CHANGED on 21 September 2026 — this app used to open on
 * Stationary.
 *
 * The mockup's own default, and it is the honest one: a stationary assignment books the trainer's
 * own diary and asks for an hour per programmed weekday, so opening on it means the common case
 * (a client training alone) starts by undoing a choice nobody made. Recorded in the slice's
 * `AGENTS.md` because it changes what a trainer who presses straight through gets.
 */
export const DEFAULT_EXECUTION_MODE: AssignExecutionMode = 'Remote';

/** Only a Stationary assignment carries times. */
export function isStationary(mode: AssignExecutionMode): boolean {
  return mode === 'Stationary';
}

/**
 * How far ahead a plan may start. `PlanAssignmentProcessor.OverrideMaxFuture` — mirrored, not
 * guessed, and the only number in this file that comes from the server rather than from the domain.
 */
export const MAX_START_DAYS_AHEAD = 730;

/** One occurrence the trainer moved. `time` is Stationary-only and `null` when untouched. */
export interface OccurrenceOverride {
  readonly date: Date;
  readonly time: string | null;
}

/** Everything the trainer chose. */
export interface AssignSelection {
  /** `null` until a client is picked. */
  readonly customerId: number | null;
  /**
   * `null` until the trainer picks one, and that is a deliberate change of 21 September 2026.
   *
   * This flow used to open on the next Monday, through a `defaultStartDate` that is deleted with
   * this change because nothing may call it. Variant D draws the projection from the start date,
   * so a defaulted date would mean the dialog opens showing a client a whole schedule nobody
   * chose, on a date nobody read. `null` is what makes the ambient illustration the honest first
   * state, and `noStartDate` is what refuses the primary until there is an answer.
   */
  readonly startDate: Date | null;
  readonly mode: AssignExecutionMode;
  /** Slot key → "HH:mm". Read only for Stationary. See `stationaryTimes.ts` for the keys. */
  readonly stationaryTimes: Readonly<Record<number, string>>;
  /**
   * The IANA zone the browser stands in, or `null` when it reports no resolvable one.
   *
   * It travels with a STATIONARY assignment and only with one: it is the clock the hours in
   * `stationaryTimes` were read off, the server freezes it on every occurrence it generates from
   * them (ADR 2026-09-08), and a remote plan chooses no hour for a zone to be about. `null` is sent
   * as an absent field rather than a default — see `shared/lib/occurrenceZone`.
   */
  readonly zoneId: string | null;
  /** Coordinate key → the move. Only occurrences the trainer actually changed appear. */
  readonly overrides: ReadonlyMap<string, OccurrenceOverride>;
  /**
   * „Zapisz też jako szablon w bibliotece" — offered for an UNSAVED plan only. Ignored on a library
   * entry, which is in the library already and whose arm refuses the flag; see `originFields`.
   */
  readonly alsoSaveAsTemplate: boolean;
}

/** Why the selection cannot be sent. Carries the numbers the copy needs; the caller words it. */
export type AssignRefusal =
  | { readonly kind: 'noClient' }
  | { readonly kind: 'noStartDate' }
  | { readonly kind: 'startTooEarly'; readonly earliest: Date }
  | { readonly kind: 'startTooLate'; readonly latest: Date }
  | { readonly kind: 'noStationaryTimes' }
  | { readonly kind: 'malformedTime'; readonly slot: number; readonly time: string }
  | { readonly kind: 'timeOffPattern'; readonly slot: number }
  | {
      readonly kind: 'overrideOutOfRange';
      readonly coordinate: OccurrenceCoordinate;
      readonly date: Date;
      readonly earliest: Date;
      readonly latest: Date;
    };

export type AssignRequestOutcome =
  | { readonly outcome: 'refused'; readonly refusals: readonly AssignRefusal[] }
  | { readonly outcome: 'ready'; readonly body: PostPlanAssignmentRequest };

/** The calendar window a date — start or override — must fall inside. Both ends are inclusive. */
export interface DateWindow {
  readonly earliest: Date;
  readonly latest: Date;
}

/** The window, from the caller's clock. Injected rather than read, so a test can pin midnight. */
export function assignDateWindow(now: Date): DateWindow {
  const today = atMidnight(now);
  return { earliest: today, latest: addDays(today, MAX_START_DAYS_AHEAD) };
}

function outsideWindow(date: Date, window: DateWindow): 'early' | 'late' | null {
  const day = atMidnight(date).getTime();
  if (day < window.earliest.getTime()) return 'early';
  if (day > window.latest.getTime()) return 'late';
  return null;
}

/** The Stationary time map as the wire's array, ascending by slot. */
function stationaryTimeInputs(times: Readonly<Record<number, string>>): StationaryTimeInput[] {
  return Object.entries(times)
    .map(([slot, time]) => ({ dayOfWeek: Number(slot), time }))
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek);
}

/**
 * The moves, as the wire's array — sorted, so the same selection always produces the same body and
 * a diff of two requests is readable.
 *
 * `time` rides along only for Stationary: the backend ignores it for the other two modes, and a
 * field that is ignored is a field that will one day stop being ignored.
 */
function occurrenceOverrideInputs(
  overrides: ReadonlyMap<string, OccurrenceOverride>,
  coordinates: ReadonlyMap<string, OccurrenceCoordinate>,
  stationary: boolean,
): OccurrenceDateOverrideInput[] {
  const inputs: OccurrenceDateOverrideInput[] = [];

  for (const [key, override] of overrides) {
    const coordinate = coordinates.get(key);
    if (coordinate === undefined) continue; // stale — see the header
    inputs.push({
      cycleIndex: coordinate.cycleIndex,
      phaseOrder: coordinate.phaseOrder,
      dayOrder: coordinate.dayOrder,
      sessionOrder: coordinate.sessionOrder,
      date: toIsoDate(override.date),
      ...(stationary && override.time !== null ? { time: override.time } : {}),
    });
  }

  return inputs.sort(
    (a, b) =>
      (a.cycleIndex ?? 0) - (b.cycleIndex ?? 0) ||
      (a.phaseOrder ?? 0) - (b.phaseOrder ?? 0) ||
      (a.dayOrder ?? 0) - (b.dayOrder ?? 0) ||
      (a.sessionOrder ?? 0) - (b.sessionOrder ?? 0),
  );
}

/**
 * A self-paced assignment: the client, the plan, and deliberately nothing else.
 *
 * ── The four fields are OMITTED, not sent as null ────────────────────────────────────────────
 * `PlanAssignmentProcessor.ResolveSelfPacedInputs` refuses a start date, a Stationary time map and a
 * per-occurrence override outright, each with `plan_self_paced_takes_no_schedule_input`, and refuses
 * Stationary or Elastic delivery with `plan_self_paced_delivery_must_be_remote`. An ABSENT delivery
 * mode is how the wire says "remote" — the server records `Remote` itself (R11) — so naming it here
 * would be this client asserting a default the server owns.
 *
 * Two of the four would pass as an explicit `null` and two would not: the backend tests the time and
 * override lists with `is { Count: > 0 }`, so an empty array is accepted, while `StartDate` is tested
 * for `is not null`. Omitting all four rather than picking per field is the rule that cannot rot when
 * one of those guards is tightened.
 *
 * The trainer's held start date, delivery mode and times are NOT cleared from the selection — they
 * survive in flow state exactly as they survive a Stationary→Remote switch. This is the one place
 * that decides they do not travel, because this is where the plan's mode is known and where a test
 * can reach the decision.
 *
 * The only thing that can still be missing is the client, so it is the only refusal.
 */
function selfPacedRequest(
  selection: AssignSelection,
  target: AssignTarget,
): AssignRequestOutcome {
  if (selection.customerId === null) {
    return { outcome: 'refused', refusals: [{ kind: 'noClient' }] };
  }

  return {
    outcome: 'ready',
    body: {
      ...originFields(target.source, selection.alsoSaveAsTemplate),
      customerId: selection.customerId,
    },
  };
}

/** Every generated coordinate, by its key — the set an override must hit. */
function coordinatesOf(cycles: readonly ScheduleCycle[]): Map<string, OccurrenceCoordinate> {
  const byKey = new Map<string, OccurrenceCoordinate>();
  for (const occurrence of flattenOccurrences(cycles)) {
    byKey.set(coordinateKey(occurrence), toCoordinate(occurrence));
  }
  return byKey;
}

/**
 * The selection as a request — or every reason it is not one.
 *
 * `now` is a parameter for the same reason `t` is elsewhere: a module that reads the clock cannot
 * be tested at a boundary, and every boundary here is a date.
 */
export function assignmentRequest(
  target: AssignTarget,
  selection: AssignSelection,
  now: Date,
  /**
   * The generated cycles, when the caller already built them for `selection.startDate`.
   *
   * A hint and never a second source of truth: the only thing read off them is the COORDINATE set
   * an override is checked against, and they are regenerated here when they are not offered.
   */
  builtCycles?: readonly ScheduleCycle[],
): AssignRequestOutcome {
  if (target.tree.schedulingMode === 'SelfPaced') return selfPacedRequest(selection, target);

  const refusals: AssignRefusal[] = [];
  const window = assignDateWindow(now);
  const stationary = isStationary(selection.mode);

  if (selection.customerId === null) refusals.push({ kind: 'noClient' });

  // A missing start date and a start date out of range are two different sentences, and conflating
  // them is how a trainer who has not answered yet gets told the plan "cannot start in the past".
  const { startDate } = selection;
  if (startDate === null) refusals.push({ kind: 'noStartDate' });
  else {
    const startProblem = outsideWindow(startDate, window);
    if (startProblem === 'early') refusals.push({ kind: 'startTooEarly', earliest: window.earliest });
    if (startProblem === 'late') refusals.push({ kind: 'startTooLate', latest: window.latest });
  }

  if (stationary) {
    const slots = new Set(stationaryTimeSlots(target.tree));
    const entries = Object.entries(selection.stationaryTimes);
    if (entries.length === 0) refusals.push({ kind: 'noStationaryTimes' });
    for (const [rawSlot, time] of entries) {
      const slot = Number(rawSlot);
      if (!slots.has(slot)) refusals.push({ kind: 'timeOffPattern', slot });
      else if (!isTimeOfDay(time)) refusals.push({ kind: 'malformedTime', slot, time });
    }
  }

  // No start date, no generated dates — so there is no coordinate set for an override to be
  // checked against, and nothing to check. The `noStartDate` refusal above already blocks the send.
  // Nothing moved is the same story: generating 364 occurrences to look nothing up in them is work
  // this function does on every keystroke in the search boxes.
  const coordinates =
    startDate === null || selection.overrides.size === 0
      ? new Map<string, OccurrenceCoordinate>()
      : coordinatesOf(builtCycles ?? buildScheduleCycles(target.tree, startDate));

  for (const [key, override] of selection.overrides) {
    const coordinate = coordinates.get(key);
    if (coordinate === undefined) continue; // dropped, not refused — see the header
    if (outsideWindow(override.date, window) !== null) {
      refusals.push({
        kind: 'overrideOutOfRange',
        coordinate,
        date: override.date,
        earliest: window.earliest,
        latest: window.latest,
      });
    }
  }

  if (refusals.length > 0) return { outcome: 'refused', refusals };

  return {
    outcome: 'ready',
    body: {
      ...originFields(target.source, selection.alsoSaveAsTemplate),
      // Narrowed by the `noClient` refusal above; asserted rather than defaulted, because a `?? 0`
      // would send a customer id that means "nobody" and read as a 404 from the server.
      customerId: selection.customerId ?? 0,
      // Narrowed by the `noStartDate` refusal, which returns before this point when it fires.
      startDate: toIsoDate(startDate ?? window.earliest),
      executionMode: selection.mode,
      stationaryTimes: stationary ? stationaryTimeInputs(selection.stationaryTimes) : null,
      // Only where an hour was actually chosen — see `AssignSelection.zoneId`. `undefined` rather
      // than `null` because the field must be ABSENT from the JSON: the server refuses a zone id it
      // cannot resolve, and reaching its own profile-zone fallback means saying nothing at all.
      ...(stationary && selection.zoneId !== null ? { zoneId: selection.zoneId } : {}),
      occurrenceOverrides: occurrenceOverrideInputs(selection.overrides, coordinates, stationary),
    },
  };
}
